import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getTenantSetupHealth } from "../../services/api";
import { useAuthStore } from "../../stores/auth-store";
import type { TenantHealthOverview } from "@common/types";
import { categoryHealth, hasTruncatedHealthItems, healthFixHref, setupPriorityTasks, tripPublicationCounts, type SetupHealthStatus } from "./setup-health";

const statusClass: Record<SetupHealthStatus, string> = {
  ready: "text-green-800 bg-green-50",
  failing: "text-red-900 bg-red-50",
  attention: "text-amber-950 bg-amber-50",
  unknown: "text-gray-700 bg-gray-100",
  notApplicable: "text-gray-600 bg-gray-50",
};
const categoryClass = {
  company: "bg-sea-50 text-sea-900",
  integrations: "bg-teal-50 text-teal-900",
  tripsBooking: "bg-amber-50 text-amber-950",
  logbook: "bg-violet-50 text-violet-900",
};

export default function TenantSetupHealth() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore(state => state.user);
  const { data, isPending, isFetching, isError, refetch } = useQuery({
    queryKey: ["tenant-setup-health", user?.tenantId, user?.id],
    enabled: user?.role === "admin",
    queryFn: async () => {
      const response = await getTenantSetupHealth();
      if (!response.success || !response.data) throw new Error("Health report unavailable");
      return response.data as TenantHealthOverview;
    },
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  if (user?.role !== "admin") return null;
  const priorities = data && !isError ? setupPriorityTasks(data) : [];
  return <section className="space-y-5" aria-labelledby="tenant-setup-health-title" aria-busy={isFetching}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="max-w-prose">
        <h2 id="tenant-setup-health-title" className="text-xl font-semibold text-gray-900">{t("ap.health.title")}</h2>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">{t("ap.health.intro")}</p>
      </div>
      <button type="button" className="btn-secondary min-h-11" disabled={isFetching} onClick={() => void refetch()}>{t(isFetching ? "ap.health.refreshing" : "ap.health.refresh")}</button>
    </div>
    {isPending && !isError && <div role="status" className="space-y-3">
      <p className="text-sm text-gray-600">{t("ap.health.loading")}</p>
      {[0, 1, 2, 3].map(index => <div key={index} aria-hidden="true" className="h-16 rounded-lg bg-gray-100" />)}
    </div>}
    {isError && <div role="alert" className="rounded-lg bg-amber-50 p-4 text-sm text-amber-950">
      <p className="font-semibold">{t("ap.health.failed")}</p>
      <p className="mt-1">{t(data ? "ap.health.staleHint" : "ap.health.failedHint")}</p>
    </div>}
    {data && <>
      <p className="text-sm text-gray-600">{t("ap.health.checkedAt", { time: new Date(data.checkedAt).toLocaleString(i18n.language.startsWith("sv") ? "sv-SE" : "en-US") })}</p>
      {!isError && <section aria-labelledby="health-next-tasks" className="space-y-3">
        <h3 id="health-next-tasks" className="font-semibold text-gray-900">{t("ap.health.prioritiesTitle")}</h3>
        {priorities.length ? <ol className="divide-y divide-gray-100 border-y border-gray-200">
          {priorities.map(task => <li key={task.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="min-w-0 flex-1"><p className="text-sm font-medium text-gray-900">{t(`ap.health.checks.${task.checkId}`)}{task.name && <> · {task.checkId === "cancellationDefaults" && ["en", "sv"].includes(task.itemId ?? "") ? t(`ap.tripeditor.details.lang.${task.itemId}`) : task.name}</>}</p><p className="mt-1 text-sm text-gray-600">{t(`ap.health.priorityReasons.${task.priority}`, { defaultValue: t("ap.health.issueNeedsReview") })}</p></div>
            <Link to={task.href} className="inline-flex min-h-11 items-center text-sm font-medium text-sea-700 underline underline-offset-4">{t("ap.health.startTask")}</Link>
          </li>)}
        </ol> : <p className="text-sm text-gray-600">{t("ap.health.noPriorityTasks")}</p>}
      </section>}
      <section aria-labelledby="health-trip-statuses" className="space-y-2">
        <h3 id="health-trip-statuses" className="font-semibold text-gray-900">{t("ap.health.tripStatuses")}</h3>
        <ul className="flex flex-wrap gap-2 text-sm">{tripPublicationCounts(data, isError).map(value => <li key={value.id} className={`rounded-md px-3 py-2 ${value.id === "activeTrips" ? "bg-sea-50 text-sea-900" : value.id === "inactiveTrips" ? "bg-amber-50 text-amber-950" : "bg-violet-50 text-violet-900"}`}><span>{t(`ap.health.publication.${value.id}`)}: </span><span className="font-semibold tabular-nums">{value.count === null ? t("ap.health.status.unknown") : value.count}</span></li>)}</ul>
      </section>
      <div className="grid items-start gap-4 md:grid-cols-2">
        {data.categories.map(category => {
          const statuses = category.checks.map(check => check.status);
          const status = isError ? "unknown" : categoryHealth(statuses);
          const attention = isError ? 0 : statuses.filter(value => value === "attention").length;
          const unknown = statuses.filter(value => value === "unknown").length;
          return <details key={category.id} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <summary className={`cursor-pointer p-4 ${categoryClass[category.id]}`}>
              <span className="inline-flex w-[calc(100%-1.5rem)] flex-wrap items-center justify-between gap-3 align-middle">
                <span><span className="block font-semibold">{t(`ap.health.categories.${category.id}`)}</span><span className="mt-1 block text-sm">{t("ap.health.categoryCounts", { attention, unknown: isError ? category.checks.length : unknown })}</span></span>
                <span className={`rounded-md px-2 py-1 text-sm font-medium ${statusClass[status]}`}>{t(`ap.health.status.${status}`)}</span>
              </span>
            </summary>
            <ul className="divide-y divide-gray-100 px-4">
              {category.checks.map(check => {
                const fixHref = healthFixHref(check.fixHref);
                const checkStatus = isError ? "unknown" : check.status;
                return <li key={check.id} className="py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 basis-64">
                      <p className="font-medium text-gray-900">{t(`ap.health.checks.${check.id}`, { defaultValue: t("ap.health.unrecognizedCheck") })}</p>
                      <p className={`mt-1 text-sm ${checkStatus === "attention" ? "text-amber-950" : "text-gray-600"}`}>{t(`ap.health.status.${checkStatus}`)}{check.count !== undefined && <> · {t("ap.health.itemCount", { count: check.count })}</>}</p>
                      {check.detail && <p className="mt-2 text-sm leading-relaxed text-gray-600">{t(`ap.health.details.${check.detail}`, { defaultValue: t("ap.health.details.sourceUnavailable") })}</p>}
                      {check.verification && <p className="mt-1 text-sm text-gray-600">{t(`ap.health.verification.${check.verification}`)}</p>}
                    </div>
                    {fixHref && <Link className="inline-flex min-h-11 items-center text-sm font-medium text-sea-700 underline underline-offset-4" to={fixHref}>{t(checkStatus === "attention" ? "ap.health.fix" : "ap.health.open")}</Link>}
                  </div>
                  {!!check.items?.length && <ul className="mt-3 space-y-3 text-sm text-gray-700">{check.items.map(item => {
                    const itemHref = healthFixHref(item.fixHref);
                    const name = check.id === "cancellationDefaults" && ["en", "sv"].includes(item.id) ? t(`ap.tripeditor.details.lang.${item.id}`) : item.name;
                    return <li key={item.id} className="break-words">
                      {itemHref ? <Link to={itemHref} className="font-medium text-sea-700 underline underline-offset-4">{name}</Link> : <span className="font-medium">{name}</span>}
                      {check.id === "activeContent" && <span className="ml-2 inline-block rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-950">{t(isError ? "ap.health.status.unknown" : "ap.health.contentNeedsReview")}</span>}
                      {!!item.issues?.length && <ul className="mt-1 list-disc space-y-1 pl-5">{item.issues.map(issue => {
                        const missing = /^missing_(?:(en|sv)_)?(.+)$/.exec(issue);
                        const field = missing?.[2];
                        const label = field === "name" ? t("ap.tripeditor.aiReview.fields.name") : field && ["description", "tagline", "longDescription", "highlights", "included", "notIncluded", "whatToBring", "cancellationPolicy"].includes(field) ? t(`ap.tripeditor.aiReview.fields.${field}`) : undefined;
                        return <li key={issue}>{missing && label ? t(missing[1] ? "ap.health.missingTranslation" : "ap.health.missingField", { field: label, language: missing[1] ? t(`ap.tripeditor.details.lang.${missing[1]}`) : "" }) : t(`ap.health.issues.${issue}`, { defaultValue: t("ap.health.issueNeedsReview") })}</li>;
                      })}</ul>}
                    </li>;
                  })}</ul>}
                  {hasTruncatedHealthItems(check.count, check.items?.length ?? 0) && <p className="mt-3 text-sm text-gray-600">{t("ap.health.truncatedItems", { shown: check.items!.length, total: check.count })}</p>}
                </li>;
              })}
            </ul>
          </details>;
        })}
      </div>
    </>}
  </section>;
}
